// @flow

import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Translate from 'client/components/translate';
import StrideHeaderWrapper from 'client/components/strideHeaderWrapper';
import SelectionList from 'client/components/list/selectionList/selectionList';
import Button from 'client/components/button/button';
import LinkButton from 'client/components/button/linkButton';
import { interpolateWith } from 'web-core/src/utils/format';
import { Tracking } from 'web-core/src/tracking';
import { save as saveUser } from 'client/actions/user';
import { extendTaxSettings, getReplacementArgs } from './utils';
import { toggleBenefit } from '../../actions/taxBenefits';
import {
  TRACKING_PREFIX,
  FILING_DISCOUNT,
  TAX_GUIDE,
  DEDUCTION_GUIDE,
  SUPPORT,
  STRIDE_TAX,
} from './constants';

const offerListPrefix = 'taxes.benefits.taxBenefits';
const benefitKeyToTrackingNameMap = {
  hrb: FILING_DISCOUNT,
  partnerGuide: TAX_GUIDE,
  deductionGuide: DEDUCTION_GUIDE,
  support: SUPPORT,
  taxApp: STRIDE_TAX,
};

const mapStateToProps: Object = (state, ownProps) => {
  const { translations, taxBenefits, user } = state;
  const translationOffers = _.get(translations, offerListPrefix, {});
  const allBenefits = _.map(translationOffers, (val, key: string) => ({
    ...val,
    id: key,
  }));

  const selectedBenefits = allBenefits.filter(item => taxBenefits.indexOf(item.id) > -1);

  return {
    user,
    translations,
    allBenefits,
    selectedBenefits,
    isSuperHost:
      _.get(user, 'settings.isSuperHost') ||
      // Airbnb sends Superhosts to the site with sh=1 query param
      _.get(ownProps.location, 'query.sh') === '1',
  };
};

const Hero = ({ hostText }) => (
  <div className="hero pattern flex-column text-center">
    <h3 className="white-color">
      <Translate path="taxes.benefits.select.hero.header" using={hostText} />
    </h3>
    <h5 className="white-color">
      <Translate path="taxes.benefits.select.hero.subheader" using={hostText} />
    </h5>
  </div>
);
Hero.propTypes = {
  hostText: PropTypes.shape({
    hostType: PropTypes.string,
    hostsType: PropTypes.string,
    discountAmount: PropTypes.string,
  }).isRequired,
};

type Props = {
  user: {
    isLoggedIn: boolean,
    settings: Object,
  },
  allBenefits: Array<Object>,
  selectedBenefits: Array<Object>,
  saveUser: Function,
  toggleBenefit: Function,
  router: {
    push: Function,
  },
  isSuperHost: boolean,
};

class SelectionPage extends React.Component<Props> {
  onChange = (selectedBenefits, changedItem) => {
    const isSelected = selectedBenefits && selectedBenefits.indexOf(changedItem) > -1;

    Tracking.track('Tax: Host: Reward Toggled', {
      Choice: benefitKeyToTrackingNameMap[changedItem.id],
      Selected: isSelected,
    });

    this.props.toggleBenefit(changedItem.id);
  };

  onContinue = (e) => {
    e.preventDefault();
    const { user, isSuperHost } = this.props;
    const taxBenefits = this.props.selectedBenefits.map(b => b.id);

    Tracking.track(`${TRACKING_PREFIX} Wanted`, {
      Choice: taxBenefits.map(id => benefitKeyToTrackingNameMap[id]).join(', ') || 'None',
    });
    Tracking.setPeopleProps({
      'Num Rewards Wanted': taxBenefits.length,
    });

    if (user && user.isLoggedIn) {
      const settings = extendTaxSettings({
        user,
        taxBenefits,
        isSuperHost,
      });

      if (!_.isEqual(settings, user.settings)) {
        this.props.saveUser({ settings }).then(() => {
          this.props.router.push('/rewards/my');
        });
      } else {
        this.props.router.push('/rewards/my');
      }
    } else {
      window.location.href = this.getAuthUrl();
    }
  };

  getRedirectUrl = () => {
    const selectedKeys = _.map(this.props.selectedBenefits, item => `selected=${item.id}`);
    let query = selectedKeys.length ? `?${selectedKeys.join('&')}` : '?selected=none';

    if (this.props.isSuperHost) {
      query += '&sh=1';
    }

    return encodeURIComponent(`tax/rewards/my${query}`);
  };

  getAuthUrl = () =>
    `/account/create?source=airbnb-tax-help${
      this.props.isSuperHost ? '-sh' : ''
    }&redirect=${this.getRedirectUrl()}`;

  render() {
    const { user, selectedBenefits, allBenefits, isSuperHost } = this.props;
    const hostText = getReplacementArgs(isSuperHost);
    const mappedAllBenefits = allBenefits.map(b => ({
      ...b,
      imageUrl: b.illustration,
      title: interpolateWith(b.title, hostText),
      subtitle: interpolateWith(b.subtitle, hostText),
    }));
    return (
      <StrideHeaderWrapper showPhone={false} logoHref="/tax/rewards" noShadow>
        <div>
          <Hero hostText={hostText} />
          <div className="hero-intruder">
            <SelectionList
              className="flush"
              allItems={mappedAllBenefits}
              selectedItems={selectedBenefits}
              changeHandler={this.onChange} />
          </div>

          <div className="flex-column pad-bottom-2">
            {user && user.isLoggedIn ? (
              <Button clickHandler={this.onContinue} className="cta-button">
                <Translate path="taxes.benefits.select.ctaLoggedIn" />
              </Button>
            ) : (
              <LinkButton className="cta-button" onClick={this.onContinue} href={this.getAuthUrl()}>
                <Translate path="taxes.benefits.select.ctaLoggedOut" />
              </LinkButton>
            )}
          </div>
        </div>
      </StrideHeaderWrapper>
    );
  }
}

// TODO: Deprecate
SelectionPage.propTypes = {
  user: PropTypes.shape({
    isLoggedIn: PropTypes.bool,
  }).isRequired,
  allBenefits: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedBenefits: PropTypes.arrayOf(PropTypes.object).isRequired,
  saveUser: PropTypes.func.isRequired,
  toggleBenefit: PropTypes.func.isRequired,
  router: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  isSuperHost: PropTypes.bool.isRequired,
};

export default connect(
  mapStateToProps,
  { toggleBenefit, saveUser },
)(SelectionPage);
